import type { ChangeEvent, ReactNode } from 'react';
import { ArrowRight, LayoutGrid, Search } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './day-category-panel.module.scss';
import { TuningEntryRow } from './tuning-entry-row';

const styles = {
  root: pickCss(css, css.root),
  search: pickCss(css, css.search),
  searchInput: pickCss(css, css.searchInput),
  shortcut: pickCss(css, css.shortcut),
  meta: pickCss(css, css.meta),
  metaLabel: pickCss(css, css.metaLabel),
  metaCount: pickCss(css, css.metaCount),
  list: pickCss(css, css.list),
  browseCta: pickCss(css, css.browseCta),
  browseCtaIcon: pickCss(css, css.browseCtaIcon),
  browseCtaLabel: pickCss(css, css.browseCtaLabel),
  browseCtaArrow: pickCss(css, css.browseCtaArrow),
};

type DayCategoryPanelProps = {
  /** e.g. 'AI RECOMMENDED · ACTIVITIES'. */
  metaLabel: string;
  /** e.g. '2 / 3 picked'. */
  pickedLabel: string;
  /** Current value of the catalog search input. */
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Bottom-row CTA text (e.g. 'Browse the full Seville activity catalog'). */
  browseCtaLabel: string;
  onBrowseAll?: () => void;
  warning?: ReactNode;
  /** The product card list. */
  children: ReactNode;
  /** Opens the global recommendation-tuning screen (pen `tunEntry`). */
  onOpenTuning?: () => void;
};

/**
 * DayCategoryPanel — pen component `B3oRd`. Body of Day picks: a single
 * catalog-search bar at the top (search OR browse — pen `eHV99`), the meta
 * label row, the recommendation-tuning entry (`tunEntry`), an optional fit
 * warning, the product card list, and a "Browse the full {city} {category}
 * catalog" CTA at the bottom (`xkL4p`). The earlier "AI Recommended /
 * Browse catalog" toggle was retired in pen 2026-05-22; search is now the
 * unified entry into catalog browsing.
 */
export const DayCategoryPanel = ({
  metaLabel,
  pickedLabel,
  searchValue,
  onSearchChange,
  browseCtaLabel,
  onBrowseAll,
  warning,
  children,
  onOpenTuning,
}: DayCategoryPanelProps) => (
  <div className={styles.root()}>
    <div className={styles.search()}>
      <Search aria-hidden="true" size={14} />
      <input
        aria-label="Search or browse the full catalog"
        className={styles.searchInput()}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onSearchChange(e.target.value)
        }
        placeholder="Search or browse the full catalog…"
        type="search"
        value={searchValue}
      />
      <span aria-hidden="true" className={styles.shortcut()}>
        ⌘K
      </span>
    </div>

    <div className={styles.meta()}>
      <span className={styles.metaLabel()}>{metaLabel}</span>
      <span className={styles.metaCount()}>{pickedLabel}</span>
    </div>

    {onOpenTuning && <TuningEntryRow onClick={onOpenTuning} />}

    {warning}

    <div className={styles.list()}>{children}</div>

    {onBrowseAll && (
      <button
        className={styles.browseCta()}
        onClick={onBrowseAll}
        type="button"
      >
        <LayoutGrid
          aria-hidden="true"
          className={styles.browseCtaIcon()}
          size={13}
        />
        <span className={styles.browseCtaLabel()}>{browseCtaLabel}</span>
        <ArrowRight
          aria-hidden="true"
          className={styles.browseCtaArrow()}
          size={12}
        />
      </button>
    )}
  </div>
);
